import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/app/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { api, ApiError, refreshSession } from "@/lib/api-client";

/**
 * Changing your own password. No permission gate: `_app` already requires a session, and that is the
 * whole requirement — this administers nobody but the person signed in.
 */
export const Route = createFileRoute("/_app/account/password")({
  component: ChangePassword,
});

function ChangePassword() {
  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tooShort = password.length > 0 && password.length < 10;
  const reused = password.length >= 10 && password === current;
  const mismatch = confirm.length > 0 && confirm !== password;
  const ready =
    current.length > 0 &&
    password.length >= 10 &&
    !reused &&
    confirm === password &&
    !busy;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api("/auth/password/change", {
        method: "POST",
        json: { currentPassword: current, password },
      });
      // The change bumped our permission version, so the access token in memory is already refused.
      // Swap it now rather than letting the next click take the 401 and the retry.
      await refreshSession();
      setCurrent("");
      setPassword("");
      setConfirm("");
      toast.success("Password changed. Other devices have been signed out.");
    } catch (e) {
      setError(
        e instanceof ApiError
          ? e.message
          : "Could not change the password. Try again."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        breadcrumbs={[
          { label: "Profile", to: "/account/profile" },
          { label: "Change password" },
        ]}
        title="Change password"
        description="Your other devices are signed out. This one stays signed in."
      />
      <Card className="max-w-md">
        <CardBody className="space-y-4">
          <Field label="Current password" htmlFor="current-password">
            <Input
              id="current-password"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          </Field>
          <Field
            label="New password"
            htmlFor="new-password"
            error={
              tooShort
                ? "At least 10 characters."
                : reused
                  ? "Choose a password you are not already using."
                  : undefined
            }
          >
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              invalid={tooShort || reused}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          <Field
            label="Confirm new password"
            htmlFor="confirm-password"
            error={mismatch ? "The two do not match." : undefined}
          >
            <Input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              invalid={mismatch}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && ready) void submit();
              }}
            />
          </Field>
          {error && <p className="text-sm text-danger-text">{error}</p>}
          <Button
            className="w-full"
            disabled={!ready}
            loading={busy}
            onClick={() => void submit()}
          >
            Change password
          </Button>
        </CardBody>
      </Card>
    </>
  );
}
